import { useState, useEffect, useRef } from 'react';
import { Loader2, Layers, GripVertical } from 'lucide-react';
import { cn } from '../../utils/cn';
import { loadGoogleMapsAPI, isGoogleMapsLoaded } from '../../utils/googleMapsLoader';

/**
 * GoogleMapPicker - Native Google Maps picker component
 *
 * Features:
 * - Native Google Maps display
 * - Draggable marker
 * - Map/Satellite toggle
 * - Click to place marker
 * - Reverse geocoding on marker drag
 */
export default function GoogleMapPicker({
  value, // { lat, lng, address }
  onChange,
  className,
  defaultCenter = { lat: -33.8688, lng: 151.2093 }, // Sydney, Australia default
  defaultZoom = 15,
  height = '300px',
  disabled = false,
  draggableMarker = true,
  showSatelliteToggle = true
}) {
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapType, setMapType] = useState('roadmap'); // 'roadmap' or 'satellite'
  const [isLoading, setIsLoading] = useState(true);

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const geocoderRef = useRef(null);
  const onChangeRef = useRef(onChange);

  // Keep onChange ref updated
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Reverse geocode to get address
  const reverseGeocode = async (lat, lng) => {
    // Ensure geocoder exists
    if (!geocoderRef.current && window.google?.maps?.Geocoder) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }

    if (!geocoderRef.current) {
      console.warn('Geocoder not available');
      return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    }

    return new Promise((resolve) => {
      geocoderRef.current.geocode(
        { location: { lat, lng } },
        (results, status) => {
          if (status === 'OK' && results && results[0]) {
            resolve(results[0].formatted_address);
          } else {
            console.warn('Geocode failed:', status);
            // If Geocoding API is not enabled, status will be 'REQUEST_DENIED'
            resolve(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
          }
        }
      );
    });
  };

  // Initialize Google Maps
  useEffect(() => {
    const initMap = async () => {
      try {
        await loadGoogleMapsAPI();
        setMapLoaded(true);
        setIsLoading(false);
      } catch (err) {
        console.error('Failed to load Google Maps:', err);
        setIsLoading(false);
      }
    };

    if (!isGoogleMapsLoaded()) {
      initMap();
    } else {
      setMapLoaded(true);
      setIsLoading(false);
    }
  }, []);

  // Create map instance
  useEffect(() => {
    if (!mapLoaded || !mapContainerRef.current || mapRef.current) return;

    const center = value?.lat && value?.lng
      ? { lat: value.lat, lng: value.lng }
      : defaultCenter;

    // Create geocoder for reverse geocoding
    geocoderRef.current = new window.google.maps.Geocoder();

    // Create map
    mapRef.current = new window.google.maps.Map(mapContainerRef.current, {
      center,
      zoom: defaultZoom,
      mapTypeId: mapType,
      disableDefaultUI: true,
      zoomControl: true,
      zoomControlOptions: {
        position: window.google.maps.ControlPosition.LEFT_BOTTOM
      },
      gestureHandling: 'greedy',
      clickableIcons: false
    });

    // Add click listener
    if (!disabled) {
      mapRef.current.addListener('click', async (e) => {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        const address = await reverseGeocode(lat, lng);
        onChangeRef.current?.({ lat, lng, address });
      });
    }

    // Create marker if value exists
    if (value?.lat && value?.lng) {
      createMarker({ lat: value.lat, lng: value.lng });
    }
  }, [mapLoaded]);

  // Update marker when value changes
  useEffect(() => {
    if (!mapRef.current) return;

    if (value?.lat && value?.lng) {
      const position = { lat: value.lat, lng: value.lng };

      if (markerRef.current) {
        markerRef.current.setPosition(position);
      } else {
        createMarker(position);
      }

      // Center map on new position
      mapRef.current.panTo(position);
    } else if (markerRef.current) {
      markerRef.current.setMap(null);
      markerRef.current = null;
    }
  }, [value?.lat, value?.lng]);

  // Update map type
  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.setMapTypeId(mapType);
    }
  }, [mapType]);

  // Create marker
  const createMarker = (position) => {
    if (!mapRef.current) return;

    // Remove existing marker
    if (markerRef.current) {
      markerRef.current.setMap(null);
    }

    // Create new marker
    markerRef.current = new window.google.maps.Marker({
      position,
      map: mapRef.current,
      draggable: draggableMarker && !disabled,
      animation: window.google.maps.Animation.DROP
    });

    // Add drag end listener
    if (draggableMarker && !disabled) {
      markerRef.current.addListener('dragend', async (e) => {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        const address = await reverseGeocode(lat, lng);
        onChangeRef.current?.({ lat, lng, address });
      });
    }
  };

  // Toggle map type
  const toggleMapType = () => {
    setMapType(prev => prev === 'roadmap' ? 'satellite' : 'roadmap');
  };

  return (
    <div className={cn('space-y-2', className)}>
      {/* Map Container */}
      <div
        className={cn(
          'relative rounded-lg overflow-hidden border border-border',
          disabled && 'opacity-50 pointer-events-none'
        )}
        style={{ height }}
      >
        {isLoading ? (
          <div className="flex items-center justify-center h-full bg-background">
            <div className="text-center">
              <Loader2 className="w-8 h-8 text-primary-orange animate-spin mx-auto mb-2" />
              <p className="text-text-secondary text-sm">Loading map...</p>
            </div>
          </div>
        ) : mapLoaded ? (
          <>
            <div
              ref={mapContainerRef}
              style={{ height: '100%', width: '100%' }}
            />

            {/* Map Type Toggle */}
            {showSatelliteToggle && (
              <div className="absolute top-2 right-2 z-10">
                <button
                  type="button"
                  onClick={toggleMapType}
                  className={cn(
                    'flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium',
                    'bg-white shadow-md border border-gray-200',
                    'hover:bg-gray-50 transition-colors'
                  )}
                  title={mapType === 'roadmap' ? 'Switch to satellite view' : 'Switch to map view'}
                >
                  <Layers className="w-3.5 h-3.5" />
                  {mapType === 'roadmap' ? 'Satellite' : 'Map'}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center justify-center h-full bg-background">
            <p className="text-text-secondary text-sm">Failed to load map</p>
          </div>
        )}
      </div>

      {/* Coordinates Display and Drag Hint */}
      {value?.lat && value?.lng && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-text-secondary">
            Coordinates: {value.lat.toFixed(6)}, {value.lng.toFixed(6)}
          </p>
          {draggableMarker && !disabled && (
            <p className="text-xs text-text-tertiary flex items-center gap-1">
              <GripVertical className="w-3 h-3" />
              Drag marker to adjust
            </p>
          )}
        </div>
      )}
    </div>
  );
}
